import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Save, Plus, X } from "lucide-react";
import { IconTextButton } from "../../../components/ButtonComponents";
import { ScaleDevice } from "../../../types/scaleDevice";

interface ScaleDeviceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  scaleDevice: ScaleDevice | null;
  onSave: (data: any) => void;
}

export const ScaleDeviceDialog: React.FC<ScaleDeviceDialogProps> = ({ isOpen, onClose, scaleDevice, onSave }) => {
  const [formData, setFormData] = useState({ 
    name: "", 
    connectionType: "TCP", 
    ipAddress: "", 
    port: 8080,
    macAddress: "", 
    dataPattern: "",
    isActive: true 
  });

  useEffect(() => {
    if (!isOpen) return;
    if (scaleDevice) {
      setFormData({
        name: scaleDevice.name,
        connectionType: scaleDevice.connectionType || "TCP",
        ipAddress: scaleDevice.ipAddress || "",
        port: scaleDevice.port || 8080,
        macAddress: scaleDevice.macAddress || "",
        dataPattern: scaleDevice.dataPattern || "",
        isActive: scaleDevice.isActive ?? true
      });
    } else {
      setFormData({ name: "", connectionType: "TCP", ipAddress: "", port: 8080, macAddress: "", dataPattern: "", isActive: true });
    }
  }, [isOpen, scaleDevice]);

  const handleSubmit = () => {
    onSave(formData);
  };

  const connectionTypeOptions = [
    { label: "TCP/IP", value: "TCP" },
    { label: "Serial", value: "SERIAL" },
    { label: "Bluetooth", value: "BLUETOOTH" },
  ];

  // Ortak input stili
  const inputClass = "w-full px-3 py-2 bg-inputBg border border-inputBorder rounded-md text-inputText focus:ring-1 focus:ring-inputFocus focus:border-inputFocus outline-none transition-all";

  return (
    <Dialog
      header={scaleDevice ? "Terazi Düzenle" : "Yeni Terazi Tanımla"}
      visible={isOpen} 
      onHide={onClose} 
      modal 
      className="w-full md:w-[500px] bg-card text-text rounded-xl shadow-lg border border-borderColor"
    >
      <div className="flex flex-col gap-4 py-4">
        {/* Terazi Adı */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-text">Terazi Adı *</label>
          <InputText 
            value={formData.name} 
            className={inputClass}
            onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} 
          />
        </div>

        {/* Bağlantı Tipi */}
        <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-text">Bağlantı Tipi</label>
            <Dropdown 
              value={formData.connectionType} 
              options={connectionTypeOptions} 
              className={inputClass + " p-0"} 
              onChange={(e) => setFormData(p => ({ ...p, connectionType: e.value }))} 
            />
        </div>

        {/* IP ve Port (Yan yana) */}
        <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-text">IP Adresi</label>
              <InputText 
                value={formData.ipAddress || ""} 
                className={inputClass}
                onChange={(e) => setFormData(p => ({ ...p, ipAddress: e.target.value }))} 
              />
            </div>
            <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-text">Port</label>
                <InputNumber 
                  value={formData.port} 
                  className={inputClass}
                  useGrouping={false}
                  onValueChange={(e) => setFormData(p => ({ ...p, port: e.value || 8080 }))} 
                />
            </div>
        </div>

        {/* MAC Adresi */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-text">MAC Adresi</label>
          <InputText 
            value={formData.macAddress || ""} 
            className={inputClass}
            onChange={(e) => setFormData(p => ({ ...p, macAddress: e.target.value }))} 
          />
        </div>

        {/* Veri Şablonu (Data Pattern) */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-text">Veri Şablonu</label>
          <InputText 
            value={formData.dataPattern || ""} 
            className={inputClass}
            placeholder="Örn: W: {weight} kg"
            onChange={(e) => setFormData(p => ({ ...p, dataPattern: e.target.value }))} 
          />
        </div>

        {/* Aktiflik */}
        <div className="flex items-center gap-2 pt-2">
          <Checkbox 
            inputId="isActive"
            checked={formData.isActive} 
            onChange={(e) => setFormData(p => ({ ...p, isActive: e.checked || false }))} 
          />
          <label htmlFor="isActive" className="text-sm text-text cursor-pointer">Terazi Aktif</label>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-2 pt-4 border-t border-borderColor mt-2">
        <IconTextButton onClick={onClose} icon={X} className="btn-cancel">İptal</IconTextButton>
        <IconTextButton onClick={handleSubmit} icon={scaleDevice ? Save : Plus} className="btn-submit">
          {scaleDevice ? "Güncelle" : "Kaydet"}
        </IconTextButton>
      </div>
    </Dialog>
  );
};
